import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import * as conversationsAPI from "../../api/conversations";
import {
  getDashboardErrorMessage,
  unwrapDashboardResponse,
} from "../../api/dashboardResponse";
import { addAsyncCases } from "../utils/asyncReducers";
import {
  paginationInitialState,
  paginationReducers,
} from "../utils/paginationReducers";

const initialState = {
  businesses: [],
  threads: [],
  threadsTotal: 0,
  selectedThread: null,
  messages: [],
  filters: {
    businessId: "",
    startDate: "",
    endDate: "",
    search: "",
    channel: "",
  },
  pagination: {
    ...paginationInitialState,
  },
  ui: {
    businessesLoading: false,
    threadsLoading: false,
    transcriptLoading: false,
  },
  requests: {
    activeThreadsRequestId: null,
    activeTranscriptRequestId: null,
  },
  status: "",
  error: null,
};

const fetchConversationBusinesses = createAsyncThunk(
  "conversations/fetchBusinesses",
  async (_, { rejectWithValue }) => {
    try {
      const res = await conversationsAPI.getConversationBusinesses();
      return unwrapDashboardResponse(res, "Failed to load businesses");
    } catch (error) {
      return rejectWithValue(
        getDashboardErrorMessage(error, "Failed to load businesses")
      );
    }
  }
);

const fetchConversationThreads = createAsyncThunk(
  "conversations/fetchThreads",
  async (params, { rejectWithValue }) => {
    try {
      const res = await conversationsAPI.getConversationThreads(params);
      return unwrapDashboardResponse(res, "Failed to load conversations");
    } catch (error) {
      return rejectWithValue(
        getDashboardErrorMessage(error, "Failed to load conversations")
      );
    }
  }
);

const fetchConversationThread = createAsyncThunk(
  "conversations/fetchThread",
  async (params, { rejectWithValue }) => {
    try {
      const res = await conversationsAPI.getConversationThread(params);
      return unwrapDashboardResponse(res, "Failed to load conversation");
    } catch (error) {
      return rejectWithValue(
        getDashboardErrorMessage(error, "Failed to load conversation")
      );
    }
  }
);

const conversationsSlice = createSlice({
  name: "conversations",
  initialState,
  reducers: {
    ...paginationReducers,
    setFilters: (state, action) => {
      state.filters = {
        ...state.filters,
        ...action.payload,
      };
    },
    setSelectedThread: (state, action) => {
      state.selectedThread = action.payload;
    },
    resetState: () => initialState,
  },
  extraReducers: (builder) => {
    addAsyncCases(builder, fetchConversationBusinesses, {
      loadingKey: ["ui", "businessesLoading"],
      onFulfilled: (state, action) => {
        state.businesses = Array.isArray(action.payload) ? action.payload : [];
      },
      onRejected: (state, action) => {
        state.businesses = [];
        toast.error(action.payload || "Failed to load businesses");
      },
    });

    builder.addCase(fetchConversationThreads.pending, (state, action) => {
      state.status = "init";
      state.ui.threadsLoading = true;
      state.ui.transcriptLoading = false;
      state.error = null;
      state.requests.activeThreadsRequestId = action.meta.requestId;
      state.requests.activeTranscriptRequestId = null;
      state.threads = [];
      state.threadsTotal = 0;
      state.selectedThread = null;
      state.messages = [];
    });
    builder.addCase(fetchConversationThreads.fulfilled, (state, action) => {
      if (state.requests.activeThreadsRequestId !== action.meta.requestId) {
        return;
      }

      const payload = action.payload || {};
      const items = Array.isArray(payload.items) ? payload.items : [];
      state.status = "idle";
      state.ui.threadsLoading = false;
      state.requests.activeThreadsRequestId = null;
      state.threads = items;
      state.threadsTotal = payload.total || 0;
      state.pagination.page = payload.page ?? state.pagination.page;
      state.pagination.rowsPerPage =
        payload.pageSize ?? state.pagination.rowsPerPage;
      if (!items.length) {
        state.selectedThread = null;
        state.messages = [];
      }
    });
    builder.addCase(fetchConversationThreads.rejected, (state, action) => {
      if (state.requests.activeThreadsRequestId !== action.meta.requestId) {
        return;
      }

      state.status = "error";
      state.ui.threadsLoading = false;
      state.requests.activeThreadsRequestId = null;
      state.threads = [];
      state.threadsTotal = 0;
      state.selectedThread = null;
      state.messages = [];
      state.error = action.payload;
      toast.error(action.payload || "Failed to load conversations");
    });

    builder.addCase(fetchConversationThread.pending, (state, action) => {
      state.status = "init";
      state.ui.transcriptLoading = true;
      state.error = null;
      state.requests.activeTranscriptRequestId = action.meta.requestId;
      state.messages = [];
    });
    builder.addCase(fetchConversationThread.fulfilled, (state, action) => {
      if (state.requests.activeTranscriptRequestId !== action.meta.requestId) {
        return;
      }

      const payload = action.payload || {};
      state.status = "idle";
      state.ui.transcriptLoading = false;
      state.requests.activeTranscriptRequestId = null;
      state.selectedThread = payload.thread || null;
      state.messages = Array.isArray(payload.messages) ? payload.messages : [];
    });
    builder.addCase(fetchConversationThread.rejected, (state, action) => {
      if (state.requests.activeTranscriptRequestId !== action.meta.requestId) {
        return;
      }

      state.status = "error";
      state.ui.transcriptLoading = false;
      state.requests.activeTranscriptRequestId = null;
      state.messages = [];
      state.error = action.payload;
      toast.error(action.payload || "Failed to load conversation");
    });
  },
});

const {
  setFilters,
  setPageNumber,
  setRowsPerPage,
  setSelectedThread,
  resetState,
} = conversationsSlice.actions;

export default conversationsSlice.reducer;

export {
  fetchConversationBusinesses,
  fetchConversationThreads,
  fetchConversationThread,
  setFilters,
  setPageNumber,
  setRowsPerPage,
  setSelectedThread,
  resetState,
};
